<template>
<!-- TODO:
   - In Date formats, minutes/seconds remain disabled.
-->
<!-- HISTORY:
   V260526.1: Added canScheduleEtl computed property and used it to disable the Schedule ETL switch.
   V260403.1: Consumed the serviceType and offsetPlaceholder props of the bt-cron-scheduler-child component.
   V260227.1: Made the NEW ETL and MANAGE SCHEDULES buttons accessible only to the Power Admin.
   V260203.1: Fixed two bugs that kept the date format value on cancel and allowed requests to be submitted without a date format.
   V260130.2: Added Filter tab + Removed computed isLocal and used the one from bt-mixin +
      In filterChanged(), Removed the getOffersCount() call because it was time-consuming and event_date was not included.
   V260113.1: Added the isSchedule switch in the General tab to hide Lake year/month dropdowns when the ETL is scheduled.
   V260109.1: Added SCHEDULE btn and consumed offsetOptions and periodOptions props to limit choices +
      Used getLsExportData() from bt-helpers.js instead of getExportData() and commented mergeJSONObjects() as it was moved into bt-mixin.js.
   V250915.1: Modified getExportData() to add $sort to aggregateQuery.
   V250808.1: [on Aref side] Made default value for debug to be false.
   V250630.1: In getExportData() removed the logic that set dateFormat from importValidations.
   V250616.1: 1st version/release.
-->
<!-- BUSINESS LOGICS:
   0- Only 'pa' can see it.
   1- No schedule button
   2- General tab: yearLake and month, hide delimiter and zip (always comma and no zip)
   3- No filter tab: should send it prepopulated.
   4- Field Definitions: Only can be set in the 1st export
      4.1- Only 4th option
      4.2- app_code, event_code, event_date and _id fields should be always mandatory (selected).
      4.3- No personal fields should be available: purl, latitude, longitude, location, ip, dedupHash, event_id, purl columns
   5- No Notifications and Actions tabs.
   6- lakeColumnsCast tab based on the field definitions
   7- lakeDateFormats tab based on the lakeColumnsCast.
-->
<v-container fluid class="px-3 py-3">
   <v-card>
      <v-card-title class="pl-2 pb-2">
         <h1 class="title font-weight-bold grey--text darken-4 pl-2" style="color:#757575 !important">
            <v-icon class="pr-2">cloud_upload</v-icon>
            <span>Lake ETLs</span>
         </h1>
         <div class="flex-grow-1"></div>
         <bt-cron-scheduler v-if="exports.length && canCreateExport"
            get-crons-on-btn-click
            btn-label="Manage Schedules"
            consumer-type="export"
            :debug="debug"
            :disabled="loadingExports"
            :is-authorized="canViewCron"
            :is-json-server="!isActualEndpoint"
            :x-small="true"
            :small="false"
         ></bt-cron-scheduler>
         <v-btn v-if="exports.length"
            x-small
            class="mr-2 mt-1"
            color="gray darken-1"
            :disabled="btnRefreshExportsDisabled"
            @click="getExports()"
         >REFRESH{{exportsDisabledDuration ? ' in ' + exportsDisabledDuration + ' sec' : ''}}
            <v-icon right dark>refresh</v-icon>
         </v-btn>
         <!--V260227: <v-btn v-if="jwt.pa || isLocal" -->
         <v-btn v-if="canCreateExport"
            x-small
            class="mr-2 mt-1"
            color="gray darken-1"
            :disabled="loadingExports || !importHeaders.length"
            @click="newExportClicked(true)"
         >New ETL
            <v-icon right dark>add</v-icon>
         </v-btn>
      </v-card-title>
      <v-card-text class="py-0">
         <bt-filter-wrapper-with-panel
            :closed-on-load="true"
            :fields="searchFields"
            :included-tabs="['standard']"
            :is-admin="jwt.pa"
            :max="searchFields.length"
            :preselected-fields="[]"
            :should-init="shouldInitFilterDefiner2"
            :std-field-values="searchFieldsValues"
            v-model="filter2"
            @filter-change="filter2Changed"
         ></bt-filter-wrapper-with-panel>
      </v-card-text>
      <v-card-text class="pt-2">
            <!-- @update:expanded="exportExpanded" -->
         <v-data-table dense fixed-header show-expand single-expand
            class="elevation-1"
            item-key="_id"
            :footer-props="{
               itemsPerPageOptions: [5, 10, 20],
               showFirstLastPage: true
            }"
            :headers="headers"
            :hide-default-footer="exportsStats.count <= 5"
            :items="exports"
            :items-per-page="5"
            :loading="loadingExports"
            :loading-text="$t('loading-text')"
            :no-data-text="$t('no-data-text', { value: 'ETLs' })"
            :no-results-text="$t('no-results-text', { value: 'ETLs' })"
            :options.sync="options"
            :search="search"
            :server-items-length="exportsStats.count"
         >
            <template v-slot:[`item.creationDate`]="{ item }">
               {{ item && item.creationDate && formatDate(item.creationDate, true) }}
            </template>
            <template v-slot:[`item.status`]="{ item }">
               <v-icon :color="getIcon(item.status).color">{{getIcon(item.status).icon}}</v-icon><span :text--color="getIcon(item.status).color"> {{ item.status }}</span>
            </template>
            <template v-slot:[`item.processedRecords`]="{ item }">
               <v-tooltip v-if="item.processedRecords"
                  top color="black"
               >
                  <template v-slot:activator="{ on, attrs }">
                     <span v-bind="attrs" v-on="on">{{formatNumber(item.processedRecords)}}</span>
                  </template>
                  <span>Processing Time: {{calculateProcessingTime(item.lastActivityDate, item.startDate)}}</span>
                  <br>
                  <span>Processing Speed: {{calculateProcessingSpeed(item.processedRecords, item.lastActivityDate, item.startDate)}} Rec/Hr</span>
               </v-tooltip>
               <span v-else>{{item.hasOwnProperty('processedRecords') ? new Intl.NumberFormat().format(item.processedRecords) : ''}}</span>
            </template>
            <template v-slot:[`item.startDate`]="{ item }">
               {{ formatDate(item.startDate, true) }}
            </template>
            <template v-slot:[`item.lastActivityDate`]="{ item }">
               {{ formatDate(item.lastActivityDate, true) }}
            </template>

            <template v-slot:expanded-item="{ item }">
               <td colspan="4" class="py-2" valign="top" dense>
                  <ul>
                     <li>
                        <span class="expanded-header">ID: </span>
                        <span class="expanded-content">{{item._id}}</span>
                     </li>
                     <li>
                        <span class="expanded-header">Year: </span>
                        <span class="expanded-content">{{item.lakeYear}}</span>
                     </li>
                     <li>
                        <span class="expanded-header">Month: </span>
                        <span class="expanded-content">{{item.lakeMonth}}</span>
                     </li>
                     <li>
                        <span class="expanded-header">Delimiter: </span>
                        <span class="expanded-content">Comma</span>
                     </li>
                     <li>
                        <span class="expanded-header">Should Zip: </span>
                        <span class="expanded-content">{{item.shouldZip}}</span>
                     </li>
                     <li>
                        <span class="expanded-header">Debug: </span>
                        <span class="expanded-content">{{item.debug ? 'true' : 'false'}}</span>
                     </li>
                     <li>
                        <span class="expanded-header">DB Name: </span>
                        <span class="expanded-content">{{item.databaseName}}</span>
                     </li>
                     <li>
                        <span class="expanded-header">Recovery Count: </span>
                        <span class="expanded-content">{{item.recoveryCount}}</span>
                     </li>
                     <li>
                        <span class="expanded-header">Recovery Error: </span>
                        <span class="expanded-content">{{item.recoveryError}}</span>
                     </li>
                  </ul>
               </td>
               <td colspan="5" class="py-2" valign="top" dense>
                  <ul>
                     <li>
                        <span class="expanded-header">Field Definitions: </span>
                        <span class="expanded-content">{{JSON.stringify(item.fieldDefinitions, null, 2)}}</span>
                     </li>
                     <li>
                        <span class="expanded-header">Filter: </span>
                        <span class="expanded-content">{{JSON.stringify(JSON.parse(item.aggregateQuery), null, 2)}}</span>
                     </li>
                     <li>
                        <span class="expanded-header">Columns Cast: </span>
                        <span class="expanded-content">{{item.lakeColumnsCast ? JSON.stringify(item.lakeColumnsCast, null, 2) : ''}}</span>
                     </li>
                     <li>
                        <span class="expanded-header">Date Formats: </span>
                        <span class="expanded-content">{{item.lakeDateFormats ? JSON.stringify(item.lakeDateFormats, null, 2) : ''}}</span>
                     </li>
                  </ul>
               </td>
            </template>

            <template v-slot:[`body.append`] v-if="exportsStats.count">
               <tr class="font-weight-bold">
                  <td colspan="5">Totals:</td>
                  <td colspan="4" align="left">{{formatNumber(exportsStats.processedTotal)}}</td>
               </tr>
            </template>
         </v-data-table>
      </v-card-text>
   </v-card>

   <v-form lazy-validation
      ref="mainForm"
      v-model="isMainFormValid"
   >
      <v-dialog no-click-animation persistent
         max-width="1080px"
         v-model="dialogExport"
      >
         <v-card flat
            :loading="loadingNewExport"
         >
            <v-card-title class="title grey--text darken-4 font-weight-bold pb-2">Create a New Lake ETL:</v-card-title>
            <v-card-text
               class="pt-2 pb-0"
            >
               <v-tabs
                  class="elevation-2"
                  background-color="grey lighten-2 accent-4"
                  slider-color="black"
                  v-model="tab"
               >
                  <v-tab 
                     :class="isGeneralFormValid ? '' : 'red--text'"
                  >General</v-tab>
                  <v-tab
                     :disabled="loadingNewExport"
                  >Filter</v-tab>
                  <v-tab
                     :class="isFieldsFormValid ? '' : 'red--text'"
                  >Field Definitions</v-tab>
                  <v-tab
                     :class="isColumnsCastFormValid ? '' : 'red--text'"
                     :disabled="!columnsCastExportFields?.length && !columnsCastErrMsg"
                  >Partial Type Casting</v-tab>
                  <!-- :disabled="!Object.keys(formData.columnsCast).length && !dateFormatsErrMsg" -->
                  <!-- V260203: Added the 2nd condition -->
                  <v-tab
                     :class="isDateFormatsFormValid && !this.dateFormatsErrMsg ? '' : 'red--text'"
                     :disabled="isDateFormatsDisabled"
                  >Date Formats</v-tab>

                  <v-tabs-items v-model="tab">
                     <v-tab-item><!-- General tab -->
                        <v-card flat tile>
                           <v-card-text>
                              <v-form lazy-validation
                                 ref="generalForm"
                                 v-model="isGeneralFormValid"
                              >
                                 <v-row>
                                    <v-col xs="12" md="2">
                                       <!-- V260526: Added disabled prop -->
                                       <v-switch dense
                                          class="py-0"
                                          label="Schedule ETL"
                                          :disabled="!canScheduleEtl"
                                          v-model="formData.isSchedule"
                                          @change="isScheduleChanged"
                                       ></v-switch>
                                    </v-col>
                                    <v-col xs="12" md="5" v-if="!formData.isSchedule">
                                       <v-select dense persistent-hint hide-selected required
                                          ref="lakeYear"
                                          hint="* Lake Year"
                                          placeholder="select a year..."
                                          :items="lakeYearItems"
                                          :rules="[rules.required]"
                                          v-model="formData.lakeYear"
                                          @change="lakeYearChanged"
                                       ></v-select>
                                    </v-col>
                                    <v-col xs="12" md="5" v-if="!formData.isSchedule">
                                       <v-select dense persistent-hint hide-selected required
                                          ref="lakeMonth"
                                          hint="* Lake Month"
                                          placeholder="select a month..."
                                          :disabled="!formData.lakeYear"
                                          :items="lakeMonthItems"
                                          :rules="[rules.required]"
                                          v-model="formData.lakeMonth"
                                       ></v-select>
                                    </v-col>
                                 </v-row>
                              </v-form>
                           </v-card-text>
                        </v-card>
                     </v-tab-item>

                     <v-tab-item><!-- Filter tab -->
                        <v-card flat class="py-0">
                           <v-card-title class="mx-0 my-0 px-0 py-0">
                              <div class="flex-grow-1"></div>
                              <bt-filter-wrapper-with-btn
                                 activeTab="standard"
                                 :bt-helpers="btHelpers"
                                 :excluded-beh-fields="['event_date']"
                                 :fields="filteredImportHeaders"
                                 :included-tabs="['standard', 'behavioral']"
                                 :is-admin="jwt.pa"
                                 :max="4"
                                 parent-component="CsExports"
                                 :preselected-fields="preselectedHeaders"
                                 :should-init="shouldInitFilterDefiner"
                                 :show-test-cases="isLocal"
                                 :std-field-values="stdFieldValues"
                                 v-model="filter"
                                 @filter-change="filterChanged"
                              ></bt-filter-wrapper-with-btn>
                           </v-card-title>
                           <v-card-text>
                              <v-textarea dense outlined persistent-hint readonly required
                                 ref="aggregateQuery"
                                 :hint="filterMsg"
                                 rows="4"
                                 :rules="[rules.required]"
                                 v-model="formData.aggregateQuery"
                              ></v-textarea>
                           </v-card-text>
                        </v-card>
                     </v-tab-item>

                     <v-tab-item><!-- Field Definitions tab -->
                        <v-card flat>
                           <v-card-text class="pb-0">
                              <v-alert dense outlined prominent text
                                 class="mb-0"
                                 border="left"
                                 :type="lastExport ? 'info' : 'warning'"
                              >
                                 {{getAlertMessage('Field Definitions')}}
                              </v-alert>
                           </v-card-text>
                        </v-card>

                        <v-card flat class="py-0">
                           <v-card-title class="mx-0 my-0 px-0 py-0">
                              <!-- TODO: Temp -->
                              <v-switch dense v-if="isLocal"
                                 class="pl-4 py-0"
                                 label="Predefine Field Definitions"
                                 v-model="predefineFieldDefinitions"
                                 @change="predefineFieldDefinitionsChanged"
                              ></v-switch>
                              <div class="flex-grow-1"></div>
                              <bt-export-field-definer is-lake
                                 class="pr-1"
                                 :debug="debug"
                                 :items="exportFields"
                                 :invalid-fields="invalidExportFields"
                                 :required-fields="requiredExportFields"
                                 v-model="formData.fieldDefinitions"                        
                                 @change="fieldDefinitionsChanged"
                              ></bt-export-field-definer>
                           </v-card-title>
                           <v-card-text>
                              <v-form lazy-validation
                                 ref="fieldsForm"
                                 v-model="isFieldsFormValid"
                              >
                                 <v-textarea dense outlined persistent-hint readonly required
                                    ref="fieldDefinitions"
                                    class="px-"
                                    hint="* Field Definitions"
                                    rows="8"
                                    :rules="[rules.requiredStringArray]"
                                    v-model="fieldDefinitions"
                                 ></v-textarea>
                              </v-form>
                           </v-card-text>
                        </v-card>
                     </v-tab-item>

                     <v-tab-item><!-- Partial Type Casting tab -->
                        <v-card flat>
                           <v-card-text class="pb-0">
                              <v-alert dense outlined prominent text
                                 class="mb-0"
                                 border="left"
                                 :type="lastExport ? 'info' : 'warning'"
                              >
                                 {{getAlertMessage('Type Casting')}}
                              </v-alert>
                           </v-card-text>
                        </v-card>

                        <v-card flat class="py-0">
                           <v-card-title class="mx-0 my-0 px-0 py-0">
                              <!-- TODO: Temp -->
                              <v-switch dense v-if="isLocal"
                                 class="pl-4 py-0"
                                 label="Populate Columns Cast"
                                 v-model="predefineColumnsCast"
                                 @change="predefineColumnsCastChanged"
                              ></v-switch>
                              <div class="flex-grow-1"></div>
                              <bt-export-field-definer is-columns-cast is-lake
                                 class="pr-1"
                                 :debug="debug"
                                 :items="columnsCastExportFields"
                                 :invalid-fields="invalidColumnsCast"
                                 v-model="formData.columnsCast"                        
                                 @change="columnsCastChanged"
                              ></bt-export-field-definer>
                           </v-card-title>
                           <v-card-text>
                              <v-form lazy-validation
                                 ref="columnsCastForm"
                                 v-model="isColumnsCastFormValid"
                              >
                                 <v-textarea dense outlined persistent-hint  required
                                    ref="columnsCast"
                                    class="px-"
                                    hint="Columns Cast"
                                    rows="4"
                                    :rules="[columnsCastCustomRule]"
                                    v-model="columnsCast"
                                 ></v-textarea>
                              </v-form>
                           </v-card-text>
                        </v-card>
                     </v-tab-item>

                     <v-tab-item><!-- Date Formats tab -->
                        <v-card flat>
                           <v-card-text class="pb-0" v-if="dateFormatsErr">
                              <v-alert dense outlined prominent text
                                 class="mb-0"
                                 border="left"
                                 type="error"
                              >
                                 {{dateFormatsErrMsg}}
                              </v-alert>
                           </v-card-text>
                           <v-card-text class="pb-0">
                              <v-alert dense outlined prominent text
                                 class="mb-0"
                                 border="left"
                                 :type="lastExport ? 'info' : 'warning'"
                              >{{getAlertMessage('Date Formats')}}
                              </v-alert>
                           </v-card-text>
                        </v-card>

                        <v-card flat class="py-0">
                           <v-card-title class="mx-0 my-0 px-0 py-0">
                              <!-- TODO: Temp -->
                              <v-switch dense v-if="isLocal"
                                 class="pl-4 py-0"
                                 label="Populate Date Formats"
                                 v-model="predefineDateFormats"
                                 @change="predefineDateFormatsChanged"
                              ></v-switch>
                              <div class="flex-grow-1"></div>
                              <div class="flex-grow-1"></div>
                              <v-tooltip left>
                                 <template v-slot:activator="{ on }">
                                    <v-btn text icon
                                       v-on="on"
                                       @click="btnDateFormatsClicked"
                                    >
                                       <v-icon>more_vert</v-icon>
                                    </v-btn>
                                 </template>
                                 <span>click to define date formats</span>
                              </v-tooltip>
                              <span>&nbsp;</span>
                           </v-card-title>
                           <v-card-text>
                              <v-textarea dense outlined persistent-hint readonly required
                                 class="py-0 my-0"
                                 ref="dateFormats"
                                 hint="Date and Timestamp Formats"
                                 rows="4"
                                 :error="dateFormatsErr"
                                 :rules="[rules.dateFormatsCustomRule]"
                                 v-model="dateFormats"
                              ></v-textarea>
                           </v-card-text>
                        </v-card>
                     </v-tab-item>

                  </v-tabs-items>
               </v-tabs>
            </v-card-text>

            <v-card-actions class="pt-0 pr-4">
               <v-switch v-if="jwt.au"
                  class="mx-0 my-0 pl-5 pt-3 pb-0"
                  label="Debug"
                  v-model="formData.debug"
               ></v-switch>
               <div class="flex-grow-1"></div>
               <v-btn text
                  color="blue darken-1"
                  @click="cancelExport"
               >Cancel</v-btn>
               <!-- <div> -->
                  <!-- <bt-cron-scheduler v-if="isLocal"
                     btn-label="BI Scheduler (for me)"
                     consumer-type="bi"
                     :debug="debug"
                     :instruction="{url: '/api/bi/v1/reports/account-download-contacts?p1=v1'}"
                     :is-authorized="canCreateCron"
                     :is-json-server="isLocal"
                     :rdls="['account-filter-servicetype','account-filter-event','admin-groupby-servicetype-account','account-download-events','parent-groupby-event-hour']"
                     v-model="cron"
                     @click="scheduleClicked"
                  ></bt-cron-scheduler> -->
                     <!-- :is-json-server="!isActualEndpoint" -->
                  <!-- V260403: consumed service-type & offset-placeholder -->
                  <bt-cron-scheduler-child v-if="formData.isSchedule"
                     btn-label="Schedule"
                     btn-icon="schedule"
                     btn-align="right"
                     btn-role="edit"
                     service-type="ls"
                     consumer-type="export"
                     dialogTitle="Add a New Schedule:"
                     offset-placeholder="Month Offset"
                     :debug="debug"
                     :btn-disabled="!isExportFormValid"
                     :is-authorized="canCreateCron"
                     :is-json-server="false"
                     :small="true"
                     :xSmall="false"
                     :show-offset="true"
                     :offset-options="['current-day','current-month','last-month']"
                     :period-options="['D','W']"
                     v-model="cron"
                     @click="scheduleClicked"
                     @cancel="scheduleCancelled"
                     @submit="scheduleSubmitted"
                  ></bt-cron-scheduler-child>
               <!-- </div> -->
               <v-btn text v-else
                  class="pl-0"
                  color="blue darken-1"
                  :disabled="!isExportFormValid"
                  @click="saveExport"
               >Submit</v-btn>
            </v-card-actions>
         </v-card>
      </v-dialog>

         <!--V260202: max-width="1100px" -->
      <v-dialog no-click-animation persistent
         max-width="480px"
         v-model="dialogDateFormats"
      >
         <v-card flat>
            <!--V260203: <v-card-title class="title grey--text darken-4 font-weight-bold pb-2">Specify Date and/or Timestamp Formats:</v-card-title> -->
            <v-card-title class="title grey--text darken-4 font-weight-bold pb-2">Specify Date Format:</v-card-title>
            <v-card-text class="pt-2 pb-0">
               <v-row>
                  <v-col cols="12">
                     <bt-date-format-definer
                        :debug="debug"
                        :has-time="false"
                        :init-form="dialogDateFormats"
                        v-model="formData.dateFormat"
                        @change="dateFormatsChanged"
                     ></bt-date-format-definer>
                  </v-col>
                  <!-- <v-col cols="6">
                     <bt-date-format-definer
                        :debug="debug"
                        :has-time="true"
                        :init-form="dialogDateFormats"
                        v-model="formData.timestampFormat"
                        @change="dateFormatsChanged"
                     ></bt-date-format-definer>
                  </v-col> -->
               </v-row>
            </v-card-text>

            <v-card-actions class="pt-0 pr-4">
               <div class="flex-grow-1"></div>
               <v-btn text
                  color="blue darken-1"
                  @click="cancelDateFormats(true)"
               >Cancel</v-btn>
               <v-btn text
                  color="blue darken-1"
                  :disabled="!isDateFormatsFormValid"
                  @click="submitDateFormats"
               >Submit</v-btn>
            </v-card-actions>

         </v-card>
      </v-dialog>
   </v-form>
</v-container>
</template>

<script>
import BtFilterWrapperWithPanel from './BtFilterWrapperWithPanel.vue';
import BtFilterWrapperWithBtn from './BtFilterWrapperWithBtn.vue';   //V260130
import BtCronScheduler from "./BtCronScheduler.vue";
import BtCronSchedulerChild from "./BtCronSchedulerChild.vue";
import BtExportFieldDefiner from "./BtExportFieldDefiner.vue";
import BtDateFormatDefiner from "./BtDateFormatDefiner.vue";
import { APIService } from '../services/cs-api-service.js';
import { BtHelpers } from '../services/bt-helpers.js';
import { addSeconds, differenceInHours, differenceInMinutes, differenceInSeconds, format, parseISO } from "date-fns";
import { isLocal } from '../mixins/bt-mixin.js';   //V260130

const NAME = 'LsExports';
const TABS = { general: 0, filter: 1, fields: 2, casting: 3, formats: 4 };
const LAKE_EXPORT_INVALID_FIELDS = ['basePurl', 'event_id', 'ip', 'latitude', 'location', 'longitude', 'purl', 'purlNumber'];
const MONTH_NAMES = ['01 - January', '02 - February', '03 - March', '04 - April', '05 - May', '06 - June', '07 - July', '08 - August', '09 - September', '10 - October', '11 - November', '12 - December'];

class FormData {
   // constructor(initData) {
   //    this.lakeYear = initData.lakeYear || 0;
   //    this.lakeMonth = initData.lakeMonth || 0;
   //    this.fieldDefinitions = initData.fieldDefinitions || [];
   //    this.columnsCast = initData.lakeColumnsCast || {};
   //    this.dateFormat = initData.lakeDateFormats?.dateformat || "";
   //    this.timestampFormat = initData.lakeDateFormats?.timestampformat || "";
   //    this.debug = false;
   //    // this.name = initData.name || '';
   //    // this.delimiter = initData.delimiter || 'csv';
   //    // this.shouldZip = initData.shouldZip || false;
   //    // this.aggregateQuery = initData.aggregateQuery || [];
   //    // this.notifications = initData.notifications || [];
   // }
   constructor() {
      this.isSchedule = false;
      this.lakeYear = 0;
      this.lakeMonth = 0;
      this.aggregateQuery = [];
      this.fieldDefinitions = [];
      this.columnsCast = {};
      this.dateFormat = "";
      this.timestampFormat = "";
      this.debug = false;
      // this.name = initData.name || '';
      // this.delimiter = initData.delimiter || 'csv';
      // this.shouldZip = initData.shouldZip || false;
      // this.aggregateQuery = initData.aggregateQuery || [];
      // this.notifications = initData.notifications || [];
   }
}

export default {
   name: NAME,

   components: {
      BtFilterWrapperWithPanel,
      BtFilterWrapperWithBtn,
      BtExportFieldDefiner,
      BtDateFormatDefiner,
      BtCronScheduler,
      BtCronSchedulerChild
   },

   props: {
      debug: {
         type: Boolean,
         default: false
      },
      isActualEndpoint: {
         type: Boolean,
         default: true
      }
   },

   data() {
      return {
         isLocal, //V260130
         jwt: {},
         apiService: null,
         btHelpers: null,
         formData: new FormData(),
         exports: [],
         rules: {
            required: value => !!value || "Value is required!",
            requiredStringArray: value => !!JSON.parse(value).length || "Field Definitions are required!",
         },
         headers: [
            { text: 'Name', value: 'name', align: 'left', sortable: true },
            { text: 'Creation Date', value: 'creationDate', align: 'left', sortable: false },
            { text: 'Creator', value: 'creator', align: 'left', sortable: true },
            { text: 'Status', value: 'status', align: 'left', sortable: true },
            { text: 'Processed Records', value: 'processedRecords', align: 'left', sortable: true },
            { text: 'Start Date', value: 'startDate', align: 'left', sortable: true },
            { text: 'Last Activity Date', value: 'lastActivityDate', align: 'left', sortable: true },
            { text: '', value: 'action', align: 'right', sortable: false }
         ],
         search: '',
         tdHeader: 'font-weight-bold font-italic',
         tdContent: 'font-italic',
         exportsDisabledDuration: 0,
         btnRefreshExportsDisabled: true,
         loadingExports: false,
         loadingNewExport: false,
         dialogExport: false,
         isMainFormValid: false,
         activeTab: 'standard',  //TODO: set to the first tab with content
         importHeaders: [],
         stdFieldValues: {},
         firstLoad: true,
         cron: {},
         options: {},
         shouldInitFilterDefiner2: false,
         filter2: {
            standard: [{ $match: {} }]
         },
         searchFields: [
            { text: 'Name', value: 'name', type: 'string', isIndexed: true },
            { text: 'Lake Year', value: 'lakeYear', type: 'number', isIndexed: true },
            { text: 'Lake Month', value: 'lakeMonth', type: 'number', isIndexed: true },
            { text: 'Creation Date', value: 'creationDate', type: 'date', isIndexed: true },
            { text: 'Status', value: 'status', type: 'string', isIndexed: true },
            { text: 'Processed Records', value: 'processedRecords', type: 'number', isIndexed: true },
            { text: 'Start Date', value: 'startDate', type: 'date', isIndexed: true },
            { text: 'Last Activity Date', value: 'lastActivityDate', type: 'date', isIndexed: true }
         ],
         searchFieldsValues: {
            status: ['waiting','processing','completed','recovery','rejected']
         },
         exportFields: [],
         fieldDefinitions: "[]",
         tab: null,//TODO: TABS.fields
         isGeneralFormValid: false,
         isFieldsFormValid: false,
         importValidations: [],
         exportsStats: {},

         // For Export to Lake feature
         lakeExportFilter: null,
         lakeYearItems: [],
         lakeMonthItems: [],
         requiredExportFields: ['_id', 'dedupHash', 'app_code', 'event_code', 'event_date'],
         invalidExportFields: [],
         lastExport: null,
         isDateFormatsFormValid: true,
         dialogDateFormats: false,
         dateFormats: '{}',
         isColumnsCastFormValid: true,
         columnsCast: '{}',
         predefineFieldDefinitions: false,   //TODO: Temp
         predefineColumnsCast: false,        //TODO: Temp
         predefineDateFormats: false,        //TODO: Temp
         newAlert: "After creating your first lake ETL, if you change any of the ##tabDescForAlerts##, you'll need to re-upload all your existing lake ETLs to keep them up to date.",
         existingAlert: "Since a lake ETL has already been created, if you change any of the ##tabDescForAlerts##, you'll need to re-upload all your existing lake ETLs to keep them up to date.",
         invalidColumnsCast: [],
         columnsCastExportFields: [],
         dateFormatsErr: false,
         dateFormatsErrMsg: '',
         // for filter tab
         filter: {},
         filterMsg: '',
         preselectedHeaders: [],
         shouldInitFilterDefiner: false,
         filteredImportHeaders: [],
         currDateFormat: ''
      }
   },

   computed: {
      token() {
         return this.$store.getters.token;
      },

      isExportFormValid() {
         //V260203: Added last condition
         return this.isGeneralFormValid && this.isFieldsFormValid && !this.dateFormatsErrMsg;
      },

      canViewCron() {
         // return (this.$store.getters.user.policies && this.$store.getters.user.policies.indexOf('scheduler-crons-list') > -1) || false;
         return !!this.$store.getters.user?.policies?.includes('scheduler-crons-list');
      },

      canCreateCron() {
         // return (this.$store.getters.user.policies && this.$store.getters.user.policies.indexOf('scheduler-crons-create') > -1) || false;
         return !!this.$store.getters.user?.policies?.includes('scheduler-crons-create');
      },

      // V260130
      // isLocal() {
      //    return window.location.href.includes('http://localhost:8181/');
      // },

      columnsCastErrMsg() {
         const defValues = new Set(this.formData.fieldDefinitions.map(def => def.value));
         if (Object.keys(this.formData.columnsCast).length) {
            const missingKeys = Object.keys(this.formData.columnsCast).filter(key => !defValues.has(key));
            if (missingKeys.length) {
               return missingKeys.join(', ') + ' Field Definitions are missing!';
            } 
         }

         return '';
      },

      columnsCastCustomRule() {
         return this.columnsCastErrMsg || true;
      },

      isDateFormatsDisabled() {
         const castValues = Object.values(this.formData.columnsCast || {});
         const hasDateOrTimestamp = castValues.includes('DATE') || castValues.includes('TIMESTAMP');
         const hasNoKeys = Object.keys(this.formData.columnsCast || {}).length === 0;
         return (hasNoKeys || !hasDateOrTimestamp) && !this.dateFormatsErrMsg;
      },

      dateFormatsCustomRule() {
         // alert('this.dateFormatsErrMsg=' + this.dateFormatsErrMsg);
         return this.dateFormatsErrMsg || true;
      },

      // V260526: Added Kushal
      // V260227
      canCreateExport() {
         return this.jwt.pa || isLocal || this.jwt.email === 'kdutta@mindfireinc.com';
      },

      // V260526
      canScheduleEtl() {
         return this.jwt.pa || isLocal;
      }
   },

   watch: {
      token() {
         this.init();
         this.nextAction();
      },

      options: {
         handler (val) {
            if (val.sortBy.length > 0) {
               const sort = {};
               sort[val.sortBy[0]] = val.sortDesc[0] ? -1 : 1;
               this.filter2.sort = sort;
               this.lakeExportFilter.sort = sort;
            }

            if (this.importHeaders.length)
               this.getExports();
         }
      },

      tab: {
         handler (val) {
            // alert('in watch: tab=' + val);
            if (val === TABS.general) {
               setTimeout(() => {
                  this.$refs.lakeYear.focus();
               }, 5);
            } else if (val === TABS.fields) {
               setTimeout(() => {
                  this.$refs.fieldsForm?.validate();
               }, 5);
            }
         }
      },

      columnsCast: {
         handler() {
            this.validateDateFormats();
         }
      },

      dateFormats: {
         handler() {
            this.validateDateFormats();
         }
      }
   },

   methods: {
      log(msg) {
         if (this.debug)
            console.log(`-----${NAME} V260526.1 says => ${msg}`);
      },

      logout() {
         this.$router.push('/');
      },

      getLakeExportFilter() {
         this.lakeExportFilter = JSON.parse(JSON.stringify(this.filter2));
         const $match = this.lakeExportFilter.standard[0].$match;
         const $matchKeys = Object.keys($match);
         if (!$matchKeys.includes('lakeYear') && !$matchKeys.includes('lakeMonth')) {
            $match.lakeYear = { "$exists": true };
         }
      },

      getLakeYearItems() {
         const currYear = new Date().getFullYear();
         for (let year = currYear; year >= 2024 ; year--) {
            this.lakeYearItems.push({ text: String(year), value: year });
         }
      },

      async init() {
         try {
            this.exports = [];
            this.lastExport = null;

            if (this.token) {
               this.jwt = JSON.parse(Buffer.from(this.token.split('.')[1], 'base64'));
               this.apiService = new APIService(this.jwt, this.token, this.debug, this.isActualEndpoint);
               this.btHelpers = new BtHelpers(this.token, this.isActualEndpoint, this.debug);
               const importData = await this.btHelpers.getImportData(this.filter2, true);
               this.importHeaders = importData.headers;
               if (this.importHeaders.length) {
                  this.filteredImportHeaders = this.importHeaders.filter(h => h.value != 'event_date');
                  await this.getExportsStats(true);

                  this.importValidations = importData.fieldValidations.filter(v => v.type === 'date');

                  this.exportFields = [...this.importHeaders, ...[
                     { text: '_id', value: '_id', type: 'string', isIndexed: true, internalField: true, required: true },
                     { text: 'app_code', value: 'app_code', type: 'string', isIndexed: true, isEventField: true, required: true },
                     { text: 'event_code', value: 'event_code', type: 'string', isIndexed: true, isEventField: true, required: true },
                     { text: 'event_date', value: 'event_date', type: 'date', isIndexed: true, isEventField: true, required: true },
                     { text: 'event_element', value: 'event_element', type: 'string', isIndexed: true, isEventField: true },
                     { text: 'event_path', value: 'event_path', type: 'string', isIndexed: true, isEventField: true },
                     { text: 'browser', value: 'browser', type: 'string', isIndexed: true, isEventField: true },
                     { text: 'device', value: 'device', type: 'string', isIndexed: true, isEventField: true },
                     { text: 'ip', value: 'ip', type: 'string', isIndexed: true, isEventField: true },
                     { text: 'os', value: 'os', type: 'string', isIndexed: true, isEventField: true },
                     { text: 'platform', value: 'platform', type: 'string', isIndexed: true, isEventField: true },
                     { text: 'event_data', value: 'event_data', type: 'number', isIndexed: false, isEventField: true },
                     { text: 'event_id', value: 'event_id', type: 'string', isIndexed: true, isEventField: true }
                  ]];

                  const importField = this.exportFields.find(f => f.value === 'importId');
                  if (importField) {
                     importField.text = importField.value;
                  }

                  if (this.importHeaders.length) {
                     this.preselectedHeaders = await this.btHelpers.getSelectedHeaders(this.importHeaders, 7);
                  }

                  const nonSystemExportFields = this.exportFields.filter(item => item.value != 'event_data' && (item.internalField || item.isEventField))
                     .map(item => item.value);
                  this.invalidExportFields = [...LAKE_EXPORT_INVALID_FIELDS, ...importData.purlColumns];

                  this.invalidColumnsCast = [Array.from(new Set(
                     ...this.invalidExportFields,
                     ...nonSystemExportFields
                  ))];

                  this.shouldInitFilterDefiner2 = true;
                  this.firstLoad = true;
               } else {
                  this.filteredImportHeaders = [];
               }
            } else
               this.jwt = {};
         } catch (error) {
            console.error('Exception in init(): ' + error.message + '\n' + JSON.stringify(error));
            alert('Exception in init(): ' + error.message);
         }
      },

      async getExportsStats(getExports) {
         this.loadingExports = true;
         let result = await this.apiService.getExportsStats(this.lakeExportFilter);
         this.loadingExports = false;
         if (result.logout)
            this.logout();

         this.exportsStats = result.message ? { count: 0 } : result.data;
         if (getExports && this.exportsStats.count)
            await this.getExports();
      },

      async nextAction() {
         const currOptions = JSON.stringify(this.options);
         const newOptions = JSON.parse(currOptions);
         newOptions.page = 1;
         if (JSON.stringify(newOptions) === currOptions)
            await this.getExports();
         else
            this.options = newOptions;
      },

      async getExports() {
         if (!this.importHeaders.length) return;

         this.loadingExports = true;
         this.btnRefreshExportsDisabled = true;
         this.exportsDisabledDuration = 30;
         let result = await this.apiService.getExports(this.lakeExportFilter, this.options.page, this.options.itemsPerPage);
         this.loadingExports = false;

         if (result.logout)
            this.logout();
         else if (result.message)
            this.exports = [];
         else {
            this.exports = result.data;
            // this.$forceUpdate();
            if (this.exports.length) {
               setTimeout(() => {
                  this.btnRefreshExportsDisabled = false;
               }, 1000 * 30);

               const setIntervalExports = setInterval(() => {
                  if (this.exportsDisabledDuration > 0)
                     this.exportsDisabledDuration--;
                  else
                     clearInterval(setIntervalExports);
               }, 1000);

               if (!this.lastExport) {
                  this.lastExport = JSON.parse(JSON.stringify(this.exports[0]));
               }
            }
         }
      },

      getIcon(status) {
         switch (status) {
            case 'waiting':
               return { color: 'grey', icon: 'schedule' };
            case 'processing':
               return { color: 'blue', icon: 'hourglass_empty' };
            case 'completed':
               return { color: 'green', icon: 'done' };
            case 'recovery':
               return { color: 'orange', icon: 'restore' };
            case 'rejected':
               return { color: 'red', icon: 'clear' };
            default:
               return { color: '', icon: '' };
         }
      },

      formatDate(date, withTime) {
         if (date) {
            // alert('in formatDate(): date=' + date + '\nwithTime=' + withTime + '\nparseISO=' + parseISO(date));
            const formattedDate = format(parseISO(date), 'M/d/yyyy h:mm:ss a');
            if (withTime) return formattedDate;
            else return formattedDate.split(' ')[0];
         }
      },

      formatNumber(number) {
         return new Intl.NumberFormat().format(number);
      },

      calculateProcessingTime(dateEnded, dateStarted, extraSeconds) {
         const isoEnded = parseISO(dateEnded);
         const isoStarted = addSeconds(parseISO(dateStarted), extraSeconds || 0);
         const h = differenceInHours(isoEnded, isoStarted);
         const min = differenceInMinutes(isoEnded, isoStarted);
         const m = min - h * 60;
         const s = differenceInSeconds(isoEnded, isoStarted) - h * 3600 - m * 60;
         let diff = '';
         if (h) diff = h + 'h ';
         if (min) diff += m + 'm ';
         diff += s + 's';
         return diff;
      },

      calculateProcessingSpeed(processedRecs, dateEnded, dateStarted, extraSeconds) {
         const cntPerHour = new Intl.NumberFormat().format(Math.round(processedRecs * 3600 / differenceInSeconds(parseISO(dateEnded), addSeconds(parseISO(dateStarted), extraSeconds || 0))));
         return cntPerHour;
      },

      async filter2Changed(filter) {
         this.filter2 = filter;
         this.getLakeExportFilter();
         await this.getExportsStats();
         this.nextAction();
      },

      async newExportClicked() {
         this.dialogExport = true;
         this.formData = new FormData();
         this.filterChanged(this.btHelpers.getInitialFilter(this.preselectedHeaders));
         this.fieldDefinitions = "[]";
         this.columnsCast = "{}";
         this.dateFormats = "{}";
         this.predefineFieldDefinitions = false;
         this.predefineColumnsCast = false;
         this.predefineDateFormats = false;
         this.shouldInitFilterDefiner = true;   //TODO:should go above this.fieldDefinitions = "[]"?

         if (this.lastExport) {
            const { fieldDefinitions, lakeColumnsCast, lakeDateFormats } = this.lastExport;
            this.formData.fieldDefinitions = fieldDefinitions;
            this.fieldDefinitionsChanged(fieldDefinitions);
            this.predefineFieldDefinitions = true;
            if (lakeColumnsCast) {
               this.formData.columnsCast = lakeColumnsCast;
               this.columnsCastChanged(lakeColumnsCast);
               this.predefineColumnsCast = true;
            }
            if (lakeDateFormats) {
               const formats = {};
               const { dateformat, timestampformat } = lakeDateFormats;

               if (dateformat) {
                  this.formData.dateFormat = dateformat;
                  formats.dateFormat = dateformat;
               }

               if (timestampformat) {
                  this.formData.dateFormat = timestampformat;
                  formats.timestampFormat = timestampformat;
               }

               this.dateFormats = JSON.stringify(formats, null, 1);
               this.predefineDateFormats = true;
            }
         }

         setTimeout(async() => {
            if (this.firstLoad) {
               this.firstLoad = false;

               if (this.importHeaders.length > 0) {
                  this.loadingNewExport = true;
                  this.stdFieldValues = await this.btHelpers.getFieldValues(this.btHelpers.getInitialFilter(), true);
                  this.loadingNewExport = false;
               }

               this.tab = TABS.fields;

               setTimeout(() => {
                  this.tab = TABS.general;
                  this.$refs.generalForm?.validate();
               }, 10);
            } else {
               this.tab = TABS.general;
               this.$refs.generalForm?.validate();
            }
         }, 5);
      },

      isScheduleChanged(val) {
         if (val) {
            this.formData.lakeYear = this.lakeYearItems[0].value + 1;
            this.formData.lakeMonth = 1;
         } else {
            this.formData.lakeYear = 0;
            this.formData.lakeMonth = 0;
            setTimeout(() => {
               this.$refs.generalForm?.validate();
            }, 10);
         }
      },

      lakeYearChanged() {
         const currDate = new Date();
         const currYear = currDate.getFullYear();
         const maxMonth = (this.formData.lakeYear === currYear) ? currDate.getMonth() + 1 : 12;
         if (this.lakeMonthItems.length !== maxMonth) {
            this.lakeMonthItems = [];
            for (let i = 1; i <= maxMonth; i++) {
               this.lakeMonthItems.push({ text: MONTH_NAMES[i - 1], value: i });
            }

            if (!this.lakeMonthItems.find(item => item.value === this.formData.lakeMonth))
               this.formData.lakeMonth = 0;
         }
         this.$refs.mainForm?.validate();
      },

      getAlertMessage(tabDesc) {
         const msg = this.lastExport ? this.existingAlert : this.newAlert;
         return msg.replace('##tabDescForAlerts##', tabDesc);
      },

      // V260130.2
      async filterChanged(filter) {
         this.filter = filter;
         this.formData.aggregateQuery = JSON.stringify(filter.standard);
         // if (this.formData.aggregateQuery === JSON.stringify(this.btHelpers.getInitialFilter().standard)) {
         //    this.filterMsg = 'please wait...';
         //    const result = await this.apiService.getCollectionInfo();
         //    const offersCount = result.data.db_records || 0;
         //    if (result.logout)
         //       this.logout();
         //    else
         //       this.filterMsg = result.timeout ? 'The number of returning records could not be shown!' : `Returns ${this.formatNumber(offersCount)} records`;
         // } else {
         //    this.filterMsg = '';
         //    // result = await this.apiService.getOffersCount(this.filter);
         //    // offersCount = result.data;
         // }
      },

      fieldDefinitionsChanged(fieldDefinitions) {
         this.fieldDefinitions = JSON.stringify(fieldDefinitions);
         const defValues = new Set(fieldDefinitions.map(def => def.value));
         this.columnsCastExportFields = this.exportFields.filter(item => defValues.has(item.value));
      },

      columnsCastChanged(columnsCast) {
         this.columnsCast = JSON.stringify(columnsCast);
      },

      //TODO: Temp
      predefineColumnsCastChanged(val) {
         if (val) {
            this.formData.columnsCast = {"transaction_date":"DATE","product":"VARCHAR","price":"DOUBLE", "account_created": "TIMESTAMP", "seed": "BOOLEAN", "app_code": "BIGINT"};
         } else {
            this.formData.columnsCast = {};
         }

         this.columnsCastChanged(this.formData.columnsCast);
      },

      cancelExport() {
         this.dialogExport = false;
         this.formData = new FormData();
      },

      scheduleClicked() {
         setTimeout(() => {
            this.cron = {
               // instruction: this.getExportData()
               instruction: this.btHelpers.getLsExportData(this.formData, this.exportFields)
            };
            this.cron.instruction.timeZone = new Date().getTimezoneOffset();
         }, 10);
      },

      scheduleCancelled() {
         this.$emit('snackbar-event', `Scheduling process was cancelled.`);
      },

      scheduleSubmitted() {
         this.$emit('snackbar-event', `'${this.formData.name}' was scheduled.`);
         this.cancelExport();
      },

      async saveExport() {
         if (!this.$refs.mainForm.validate()) return;
         
         this.loadingNewExport = true;
         //V260109: const exportData = this.getExportData();
         const exportData = this.btHelpers.getLsExportData(this.formData, this.exportFields);
         // console.warn('aggregateQuery=' + exportData.aggregateQuery)
         const result = await this.apiService.createExport(exportData);
         this.loadingNewExport = false;
         if (result.logout)
            this.logout();
         else if (!result.message) {
            // alert('result=' + JSON.stringify(result))
            this.$emit('snackbar-event', `'${exportData.name}' was submitted.`);
            this.cancelExport();
            this.exports.unshift(result.data);
            this.exportsStats.count++;
            this.lastExport = JSON.parse(JSON.stringify(this.exports[0]));
         }
      },

      /* V260109: Moved into bt-helpers.js */
      // getExportData() {
      //    this.log('in getExportData(): formData=' + JSON.stringify(this.formData));
      //    const { lakeYear, lakeMonth, fieldDefinitions, debug, columnsCast, dateFormat, timestampFormat } = this.formData;
      //    const exportData = {
      //       lakeYear,
      //       lakeMonth,
      //       fieldDefinitions,
      //       debug,
      //       delimiter: 'csv',
      //       shouldZip: false
      //       // name
      //       // aggregateQuery
      //       // lakeColumnsCast
      //       // lakeDateFormats
      //    };

      //    // V250630:
      //    // this.importValidations.forEach(v => {
      //    //    const fld = exportData.fieldDefinitions.find(f => f.label === v.column);
      //    //    if (fld) {
      //    //       fld.dateFormat = v.format;
      //    //    }
      //    // });

      //    const eventFields = {};
      //    const otherFields = {};
      //    const vars = [];
      //    exportData.fieldDefinitions.forEach(fld => {
      //       if (fld.value)
      //          vars.push(fld.value);
      //       else 
      //          vars.push(...fld.format.slice(1));
      //    });
      //    const uniqueVars = [...new Set(vars)];
      //    uniqueVars.forEach(v => {
      //       const field = this.exportFields.find(f => f.value === v);

      //       if (field) {
      //          if (field.isEventField) {
      //             eventFields[field.value] = `$events.${field.value === 'event_id' ? '_id' : field.value}`;
      //          } else {
      //             otherFields[field.value] = 1;
      //          }
      //       } else {
      //          const edField = exportData.fieldDefinitions.find(f => f.value === v);
      //          if (edField)
      //             eventFields[edField.value] = '$events.event_data.' + edField.value;
      //       }
      //    });

      //    const lakeMonthStr = exportData.lakeMonth.toString().padStart(2, '0');
      //    const daysInMonthStr = getDaysInMonth(new Date(exportData.lakeYear, exportData.lakeMonth - 1)).toString().padStart(2, '0');
      //    exportData.name = `${exportData.lakeYear}-${lakeMonthStr}`;
      //    const aggregateQuery = JSON.parse(JSON.stringify(LAKE_EXPORT_AGGREGATE_QUERY)
      //       .replace(/lakeYear/g, exportData.lakeYear)
      //       .replace(/lakeMonth/g, lakeMonthStr)
      //       .replace(/lakeDay/g, daysInMonthStr)
      //    );

      //    const otherFieldsKeys = Object.keys(otherFields);
      //    if (otherFieldsKeys.length) {
      //       const projectObj = aggregateQuery.find(q => q.$project);
      //       if (projectObj) {
      //          const $project = projectObj.$project;
      //          const $projectKeys = Object.keys($project);
      //          otherFieldsKeys.forEach(key => {
      //             if (!$projectKeys.includes(key))
      //                $project[key] = 1;
      //          });
      //       }
      //    }

      //    if (Object.keys(eventFields).length)
      //       aggregateQuery.push({ $unwind: '$events' });

      //    aggregateQuery.push({ $project: mergeJSONObjects(otherFields, eventFields) });

      //    // V250915
      //    aggregateQuery.push({
      //       $sort: {
      //          event_code: 1,
      //          event_date: 1
      //       }
      //    });

      //    exportData.aggregateQuery = JSON.stringify(aggregateQuery);
         
      //    if (Object.keys(columnsCast).length)
      //       exportData.lakeColumnsCast = columnsCast;

      //    if (dateFormat || timestampFormat) {
      //       exportData.lakeDateFormats = {};
      //       if (dateFormat)
      //          exportData.lakeDateFormats.dateformat = dateFormat;
      //       if (timestampFormat)
      //          exportData.lakeDateFormats.timestampformat = timestampFormat;
      //    }

      //    this.log('in getExportData(): exportData=' + JSON.stringify(exportData));
      //    return exportData;
      // },

      /* V260109: Moved into bt-mixin.js */
      // mergeJSONObjects(obj1, obj2) {
      //    if (Object.keys(obj1).length === 0) {
      //       return obj2;
      //    }
      //    if (Object.keys(obj2).length === 0) {
      //       return obj1;
      //    }
      //    return { ...obj1, ...obj2 };
      // },

      //TODO: Temp
      predefineFieldDefinitionsChanged(val) {
         if (val) {
            this.formData.fieldDefinitions = [{"label":"Transaction_Date","value":"transaction_date","default":""},{"label":"Product","value":"product","default":""},{"label":"Price","value":"price","default":""},{"label":"Payment_Type","value":"payment_type","default":""},{"label":"City","value":"city","default":""},{"label":"State","value":"state","default":""},{"label":"Country","value":"country","default":""},{"label":"Account_Created","value":"account_created","default":""},{"label":"Last_Login","value":"last_login","default":""},{"label":"Email","value":"email","default":""},{"label":"Seed","value":"seed","default":""},{"label":"ID","value":"id","default":""},{"label":"_id","value":"_id","default":""},{"label":"app_code","value":"app_code","default":""},{"label":"event_code","value":"event_code","default":""},{"label":"event_date","value":"event_date","default":""}];
            this.fieldDefinitionsChanged(this.formData.fieldDefinitions);
         }
      },

      btnDateFormatsClicked() {
         this.currDateFormat = this.formData.dateFormat; //V260203
         this.isDateFormatsFormValid = false;
         this.dialogDateFormats = true;
      },

      dateFormatsChanged(val) {
         this.isDateFormatsFormValid = val;
      },

      submitDateFormats() {
         const formats = {};
         if (this.formData.dateFormat)
            formats.dateFormat = this.formData.dateFormat;
         if (this.formData.timestampFormat)
            formats.timestampFormat = this.formData.timestampFormat;

         this.dateFormats = Object.keys(formats).length ? JSON.stringify(formats, null, 1) : '{}';
         this.cancelDateFormats(false);
      },

      // V260203: Added param
      cancelDateFormats(isCancelled) {
         this.isDateFormatsFormValid = true;
         this.dialogDateFormats = false;
         if (isCancelled) {
            this.formData.dateFormat = this.currDateFormat;
         }
      },

      //TODO: Temp
      predefineDateFormatsChanged(val) {
         if (val) {
            this.formData.dateFormat = "%-m-%y-%-d";
            this.formData.timestampFormat = "%Y/%d/%m %-H:%-M:%-S";
            this.dateFormats = JSON.stringify({
               dateFormat: this.formData.dateFormat,
               timestampFormat: this.formData.timestampFormat
            }, null, 1);
         } else {
            this.formData.dateFormat = "";
            this.formData.timestampFormat = "";
            this.dateFormats = '{}';
         }
      },

      validateDateFormats() {
         let errMsg = '';
         const missing = [];
         const formats = JSON.parse(this.dateFormats);
         const ccValues = Object.values(JSON.parse(this.columnsCast));
         const hasDate = ccValues.includes("DATE");
         if (hasDate && !formats.dateFormat) missing.push('Date');
         const hasTimestamp = ccValues.includes("TIMESTAMP");
         if (hasTimestamp && !formats.timestampFormat) missing.push('Timestamp');
         let len = missing.length;
         if (len === 1) errMsg = missing[0] + ' format is required!';
         else if (len === 2) errMsg = 'Both ' + missing.join(' and ') + ' formats are required!';
         else {
            const extras = [];
            if (!hasDate && formats.dateFormat) extras.push('dateFormat');
            if (!hasTimestamp && formats.timestampFormat) extras.push('Timestamp');
            len = extras.length;
            if (len === 1) errMsg = extras[0] + ' should not be specified!';
            else if (len === 2) errMsg = 'None of ' + extras.join(' and ') + ' should not be specified!';
         }

         this.isDateFormatsFormValid = errMsg === '';
         this.dateFormatsErr = !this.isDateFormatsFormValid;
         this.dateFormatsErrMsg = errMsg;
      },
   },

   created() {
      this.getLakeExportFilter();
      this.getLakeYearItems();
      this.searchFieldsValues = {
         lakeYear: this.lakeYearItems.map(item => item.value),
         lastMonth: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
      };
      this.init();
   }
}
</script>

<style scoped>
.expanded-header {
   font-style: italic;
   font-weight: bold;
}
</style>
